/**
 * What a player does to redstone components: right-click (lever, button,
 * repeater delay, comparator mode, note pitch, daylight detector mode) and
 * which state a placed component starts in. Called from game/blockActions.ts,
 * so it runs the same offline, on the server and in the client's prediction.
 */
import { IS_SOLID, faceIndex } from '../../blocks';
import { K, RS_KIND, RS_POW, RS_VAR, rsId, withPow, withVar } from './defs';
import { noteData, type BlockReader } from './power';

interface UseEnv {
  readonly world: BlockReader;
  canEdit(x: number, y: number, z: number): boolean;
  setBlock(x: number, y: number, z: number, id: number): void;
  fx(kind: 'note' | 'click', x: number, y: number, z: number, d: number): void;
}

/** Does a right-click act on this block? */
export function redstoneInteractable(id: number): boolean {
  const k = RS_KIND[id];
  return k === K.LEVER || k === K.BUTTON || k === K.REPEATER || k === K.COMPARATOR || k === K.NOTE || k === K.DAYLIGHT;
}

/** Right-click on a component; true when it did something. */
export function useRedstone(env: UseEnv, x: number, y: number, z: number, id: number): boolean {
  if (!redstoneInteractable(id) || !env.canEdit(x, y, z)) return false;
  switch (RS_KIND[id]) {
    case K.LEVER: {
      const on = RS_POW[id] === 1 ? 0 : 1;
      env.setBlock(x, y, z, withPow(id, on));
      env.fx('click', x, y, z, on);
      return true;
    }
    case K.BUTTON:
      if (RS_POW[id] === 1) return true; // already in
      env.setBlock(x, y, z, withPow(id, 1));
      env.fx('click', x, y, z, 1);
      return true;
    case K.REPEATER:
      env.setBlock(x, y, z, withVar(id, (RS_VAR[id] + 1) % 4));
      return true;
    case K.COMPARATOR:
      env.setBlock(x, y, z, withVar(id, 1 - RS_VAR[id]));
      env.fx('click', x, y, z, 1 - RS_VAR[id]);
      return true;
    case K.DAYLIGHT:
      env.setBlock(x, y, z, withVar(id, 1 - RS_VAR[id]));
      return true;
    case K.NOTE: {
      const next = withVar(id, (RS_VAR[id] + 1) % 25);
      env.setBlock(x, y, z, next);
      if (env.world.getBlock(x, y + 1, z) === 0) env.fx('note', x, y, z, noteData(env.world, x, y, z, next));
      return true;
    }
    default: return false;
  }
}

/**
 * The state a component selected as `sel` goes in as, or `undefined` when
 * `sel` is not an oriented redstone component, or -1 when it cannot go there.
 * (nx, ny, nz) is the clicked face's normal, (lx, ly, lz) where the player looks.
 */
export function redstonePlaceId(sel: number, nx: number, ny: number, nz: number, lx: number, ly: number, lz: number): number | undefined {
  const k = RS_KIND[sel];
  if (k === K.NONE || k === K.TORCH) return undefined;
  const ax = Math.abs(lx), ay = Math.abs(ly), az = Math.abs(lz);
  const lookH = ax >= az ? (lx >= 0 ? 0 : 1) : (lz >= 0 ? 4 : 5);
  const look = ay > ax && ay > az ? (ly > 0 ? 2 : 3) : lookH;
  switch (k) {
    case K.LEVER: case K.BUTTON: return rsId(k, faceIndex(-nx, -ny, -nz), 0, RS_VAR[sel]);
    case K.HOOK: return ny !== 0 ? -1 : rsId(k, faceIndex(-nx, 0, -nz), 0, 0, 0);
    case K.REPEATER: return rsId(k, lookH, 0, 0, 0);
    case K.COMPARATOR: return rsId(k, lookH, 0, 0);
    case K.OBSERVER: return rsId(k, look, 0);
    case K.PISTON: return rsId(k, look ^ 1, 0, RS_VAR[sel], 0);
    default: return undefined;
  }
}

/** Dust, plates, repeaters and comparators stand on a solid block (their support rule covers the rest). */
export function redstoneFloorOk(w: BlockReader, sel: number, x: number, y: number, z: number): boolean {
  const k = RS_KIND[sel];
  if (k !== K.DUST && k !== K.PLATE && k !== K.REPEATER && k !== K.COMPARATOR) return true;
  return IS_SOLID[w.getBlock(x, y - 1, z)] === 1;
}
